/**
 * Longshade reference forms — AJAX submission to Shopify's native /contact endpoint.
 *
 * - Intercepts every form flagged [data-longshade-reference-form] that posts (GET
 *   forms such as the location finder are left untouched).
 * - Keeps the visitor on the page: no redirect to /pages/contact-us on submit.
 * - Sends submissions to Shopify's native contact endpoint, which emails the
 *   store's configured sender email. This does not create a customer profile.
 * - Forms whose inputs are already named contact[...] (careers) pass through
 *   unchanged; all other reference forms are renamed to contact[...] on the fly,
 *   and a contact[body] summary is always attached so nothing is lost.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var SUCCESS_HTML =
    '<div class="ls-form-success" role="status">' +
    "Thank you! Your submission has been received — our team will get back to you shortly." +
    '</div>';
  var ERROR_TEXT =
    'Sorry, something went wrong while submitting the form. Please try again.';
  var VERIFY_ERROR_TEXT =
    'Security verification could not be loaded. Please refresh the page and try again.';
  // Shopify storefront captcha sitekey — required by POST /contact.
  var HCAPTCHA_SITEKEY = 'f06e6c50-85a8-45c8-87d0-21a2b65856fe';

  function loadHcaptcha() {
    return new Promise(function (resolve) {
      if (window.hcaptcha) return resolve(window.hcaptcha);
      if (!document.getElementById('ls-hcaptcha-api')) {
        var script = document.createElement('script');
        script.id = 'ls-hcaptcha-api';
        script.src =
          'https://js.hcaptcha.com/1/api.js?render=explicit&recaptchacompat=off';
        script.async = true;
        document.head.appendChild(script);
      }
      var waited = 0;
      (function check() {
        if (window.hcaptcha) return resolve(window.hcaptcha);
        waited += 200;
        if (waited > 15000) return resolve(null);
        setTimeout(check, 200);
      })();
    });
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (resolve) { setTimeout(function () { resolve(null); }, ms); })
    ]);
  }

  function captchaToken(form) {
    return loadHcaptcha().then(function (hcaptcha) {
      if (!hcaptcha) return null;
      try {
        if (form.dataset.lsCaptchaId === undefined) {
          var holder = document.createElement('div');
          holder.className = 'ls-hcaptcha-holder';
          form.appendChild(holder);
          form.dataset.lsCaptchaId = hcaptcha.render(holder, {
            sitekey: HCAPTCHA_SITEKEY,
            size: 'invisible'
          });
        }
        var id = form.dataset.lsCaptchaId;
        if (hcaptcha.reset) hcaptcha.reset(id);
        // 挑战出现时滚动到验证区提示用户（隐形通过则无感知）
        var holderEl = form.querySelector('.ls-hcaptcha-holder');
        if (holderEl && !holderEl.dataset.lsWatch) {
          holderEl.dataset.lsWatch = 'true';
          new MutationObserver(function () {
            var ch = holderEl.querySelector('iframe');
            if (ch && ch.getBoundingClientRect().height > 60) {
              showStatus(form, 'Please complete the security verification below to continue.', true);
              holderEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }).observe(holderEl, { childList: true, subtree: true });
        }
        return withTimeout(
          hcaptcha.execute(id, { async: true }).then(function (result) {
            return (result && result.response) || null;
          }),
          60000
        );
      } catch (err) {
        return null;
      }
    });
  }

  function formTitle(form) {
    var match = /sb-form-([a-z0-9-]+)/i.exec(form.className);
    if (!match) return 'Website form';
    return match[1]
      .replace(/-/g, ' ')
      .replace(/\b[a-z]/g, function (c) {
        return c.toUpperCase();
      });
  }

  function fieldLabel(form, input) {
    if (input.id) {
      var label = form.querySelector('label[for="' + input.id + '"]');
      if (label) return label.textContent.trim();
    }
    return input.name;
  }

  function wrapperOf(input) {
    return input.closest('.sb-formfield') || input.parentElement;
  }

  function clearStatus(form) {
    form.querySelectorAll('.form-error').forEach(function (el) {
      el.classList.remove('form-error');
    });
    form.querySelectorAll('.ls-form-status').forEach(function (el) {
      el.remove();
    });
  }

  function showStatus(form, text, isError) {
    var box = document.createElement('div');
    box.className =
      'ls-form-status' + (isError ? ' ls-form-status--error' : '');
    box.setAttribute('role', 'alert');
    box.textContent = text;
    var inner = form.querySelector('.form-inner') || form;
    inner.insertBefore(box, inner.firstChild);
    return box;
  }

  function validate(form) {
    var errors = [];
    var firstBad = null;
    var fields = form.querySelectorAll('input[name], select[name], textarea[name]');
    fields.forEach(function (input) {
      var wrapper = wrapperOf(input);
      var required =
        (wrapper && wrapper.classList.contains('form-required')) ||
        input.hasAttribute('required');
      var value = (input.value || '').trim();
      if (input.type === 'file') return;
      if (required && !value) {
        wrapper && wrapper.classList.add('form-error');
        errors.push(fieldLabel(form, input) + ' is required.');
        if (!firstBad) firstBad = input;
        return;
      }
      if (value && input.type === 'email' && !EMAIL_RE.test(value)) {
        wrapper && wrapper.classList.add('form-error');
        errors.push('Please enter a valid email address.');
        if (!firstBad) firstBad = input;
      }
    });
    return { messages: errors, firstBad: firstBad };
  }

  function buildBody(form) {
    var lines = [formTitle(form) + ' — submission from longshade.sg'];
    form.querySelectorAll('input[name], select[name], textarea[name]').forEach(
      function (input) {
        var name = input.name;
        if (!name || input.disabled) return;
        if (input.type === 'file') {
          if (input.files && input.files.length) {
            lines.push(fieldLabel(form, input) + ': [attached] ' + input.files[0].name);
          }
          return;
        }
        if (/^(form_type|utf8|form_id|site_id|page_id|previous_form_id|_wpnonce)$/.test(name)) return;
        var value = (input.value || '').trim();
        if (!value) return;
        lines.push(fieldLabel(form, input) + ': ' + value);
      }
    );
    return lines.join('\n');
  }

  function buildPayload(form) {
    var payload = new FormData();
    payload.append('contact[body]', buildBody(form));

    var firstName = '';
    var lastName = '';
    form.querySelectorAll('input[name], select[name], textarea[name]').forEach(
      function (input) {
        var name = input.name;
        if (!name || input.disabled || input.type === 'file') return;
        if (/^(form_type|utf8|form_id|site_id|page_id|previous_form_id|_wpnonce)$/.test(name)) return;
        var value = (input.value || '').trim();
        if (!value) return;

        if (/^contact\[/.test(name)) {
          if (!payload.has(name)) payload.append(name, value);
        } else {
          var key = 'contact[' + name + ']';
          if (!payload.has(key)) payload.append(key, value);
        }
        if (/first[_-]?name/i.test(name)) firstName = value;
        if (/last[_-]?name/i.test(name)) lastName = value;
      }
    );

    if (firstName || lastName) {
      payload.append('contact[name]', (firstName + ' ' + lastName).trim());
    }
    if (!payload.has('form_type')) payload.append('form_type', 'contact');
    return payload;
  }

  function submitUrl(form) {
    var action = form.getAttribute('action') || '/contact';
    try {
      var url = new URL(action, window.location.origin);
      if (!/\/contact\/?$/.test(url.pathname)) url = new URL('/contact', window.location.origin);
      return url.toString();
    } catch (err) {
      return '/contact';
    }
  }

  function setSubmitting(form, submitting) {
    var buttons = form.querySelectorAll('input[type="submit"], button[type="submit"], .submit .button');
    buttons.forEach(function (button) {
      if (submitting) {
        button.dataset.lsOriginal = button.value || button.textContent;
        button.value = 'Submitting…';
        button.textContent = 'Submitting…';
      } else {
        if (button.dataset.lsOriginal) {
          button.value = button.dataset.lsOriginal;
          button.textContent = button.dataset.lsOriginal;
        }
      }
      button.disabled = submitting;
    });
  }

  function isSuccessfulSubmission(response) {
    if (!response || !response.ok) return false;
    try {
      var resultUrl = new URL(response.url);
      return resultUrl.origin === window.location.origin &&
        resultUrl.searchParams.get('contact_posted') === 'true';
    } catch (err) {
      return false;
    }
  }

  function onSuccess(form) {
    var showDirectChannels = form.dataset.lsConversionForm === 'true';
    form.innerHTML = SUCCESS_HTML;
    form.classList.add('ls-form-submitted');
    var box = form.querySelector('.ls-form-success');
    if (box && showDirectChannels) {
      var followUp = document.createElement('div');
      followUp.className = 'ls-form-success__follow-up';
      var note = document.createElement('p');
      note.textContent = 'Prefer to talk directly? WhatsApp or call our Singapore team to discuss your space. Home visits are arranged by appointment, with no obligation to buy.';
      var actions = document.createElement('div');
      actions.className = 'ls-form-success__actions';
      var whatsapp = document.createElement('a');
      whatsapp.href = 'https://api.whatsapp.com/send?phone=6593895689&text=' + encodeURIComponent('Hello Longshade, I have sent an enquiry and would like to discuss a no-obligation home measurement and quotation. Please help arrange an appointment.');
      whatsapp.target = '_blank';
      whatsapp.rel = 'noopener noreferrer';
      whatsapp.textContent = 'Continue on WhatsApp';
      var phone = document.createElement('a');
      phone.href = 'tel:+6593895689';
      phone.textContent = 'Call +65 9389 5689';
      actions.appendChild(whatsapp);
      actions.appendChild(phone);
      followUp.appendChild(note);
      followUp.appendChild(actions);
      box.appendChild(followUp);
    }
    if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function onFailure(form, message) {
    setSubmitting(form, false);
    showStatus(form, message || ERROR_TEXT, true);
  }

  function handleSubmit(form, event) {
    event.preventDefault();
    clearStatus(form);

    var result = validate(form);
    if (result.messages.length) {
      var box = showStatus(
        form,
        'Please complete the required fields highlighted below.',
        true
      );
      var firstBad = result.firstBad;
      if (firstBad) {
        try {
          firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () { try { firstBad.focus(); } catch (e) {} }, 350);
        } catch (e) {}
      } else if (box && box.scrollIntoView) {
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(form, true);

    var attempt = 0;

    function trySubmit() {
      attempt += 1;
      return captchaToken(form)
        .catch(function () {
          return null;
        })
        .then(function (token) {
          var payload = buildPayload(form);
          if (token) payload.append('h-captcha-response', token);
          return fetch(submitUrl(form), {
            method: 'POST',
            body: payload,
            credentials: 'same-origin',
            headers: { Accept: 'text/html' }
          }).then(function (response) {
            // A redirect can lead to a challenge or error page. Only Shopify's
            // explicit contact success marker confirms that it accepted the form.
            if (isSuccessfulSubmission(response)) return 'success';
            // 第一次失败：换一个新 token 自动重试一次（hCaptcha 评分偶发拒绝）
            if (attempt === 1 && token) return 'retry';
            return token ? 'failed' : 'no-token';
          });
        });
    }

    trySubmit()
      .then(function (outcome) {
        if (outcome === 'retry') {
          return new Promise(function (res) { setTimeout(res, 1500); }).then(trySubmit);
        }
        return outcome;
      })
      .then(function (outcome) {
        if (outcome === 'success') onSuccess(form);
        else onFailure(form, outcome === 'no-token' ? VERIFY_ERROR_TEXT : '');
      })
      .catch(function () {
        onFailure(form);
      });
  }

  function init() {
    var forms = document.querySelectorAll(
      'form[data-longshade-reference-form="true"]'
    );
    forms.forEach(function (form) {
      var method = (form.getAttribute('method') || 'get').toLowerCase();
      if (method !== 'post') return;
      if (!form.classList.contains('sb-form-careers')) {
        form.dataset.lsConversionForm = 'true';
      }
      if (form.dataset.lsReferenceBound) return;
      form.dataset.lsReferenceBound = 'true';
      form.addEventListener('submit', function (event) {
        handleSubmit(form, event);
      });
    });
    initServiceRequest();
  }

  /* Service Request 页：initial Yes/No -> main form；Indoors 条件字段 */
  function initServiceRequest() {
    var sel = document.getElementById('purchased-from-longshade');
    if (sel && !sel.dataset.lsSrBound) {
      sel.dataset.lsSrBound = 'true';
      sel.addEventListener('change', function () {
        var errorBox = document.getElementById('not-longshade-error');
        var mainForm = document.querySelector('.service-right.main-form');
        var initial = sel.closest('.initial-form');
        if (sel.value === 'yes') {
          if (errorBox) errorBox.classList.remove('show');
          if (initial) initial.style.display = 'none';
          if (mainForm) {
            mainForm.classList.add('show');
            mainForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else if (sel.value === 'no') {
          if (errorBox) errorBox.classList.add('show');
        } else if (errorBox) {
          errorBox.classList.remove('show');
        }
      });
    }
    var indoor = document.getElementById(
      'service_form_is-your-product-installed-indoors-or-outdoors'
    );
    if (indoor && !indoor.dataset.lsSrBound) {
      indoor.dataset.lsSrBound = 'true';
      indoor.addEventListener('change', function () {
        var box = document.getElementById('indoor-fields');
        if (!box) return;
        if (indoor.value === 'Indoors') box.classList.add('show');
        else box.classList.remove('show');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
